import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Agents, Probes, Services as ServicesApi } from '../lib/api';
import { Stat, Pill, Spinner, ProgressBar, EmptyState } from '../components/Common';
import {
  Area, AreaChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis, BarChart, Bar,
} from 'recharts';
import type {
  AgentSummary, HttpProbeConfig, ServiceWatchConfig,
} from '@freebuff/shared';
import clsx from '../lib/clsx';

interface SystemPoint { ts: number; cpuPct: number; memPct: number; load1: number; }
interface DiskPoint { ts: number; mount: string; pct: number; used: number; total: number; }
interface NetPoint { ts: number; iface: string; rxSec: number; txSec: number; }
interface ProcPoint { ts: number; pid: number; name: string; cpuPct: number; memPct: number; cmd: string; }
interface DockerPoint { ts: number; containerId: string; name: string; status: string; cpuPct: number; memUsed: number; memLimit: number; }
interface HttpPoint { ts: number; probeId: number; url: string; statusCode: number | null; latencyMs: number | null; ok: boolean; sslDaysLeft: number | null; error: string | null | undefined; }

type Range = '15m' | '1h' | '6h' | '24h';
const RANGES: Record<Range, { from: () => number; step: number; bucket: string }> = {
  '15m': { from: () => Date.now() - 15 * 60_000, step: 5_000, bucket: '5s' },
  '1h':  { from: () => Date.now() - 60 * 60_000, step: 30_000, bucket: '30s' },
  '6h':  { from: () => Date.now() - 6 * 60 * 60_000, step: 120_000, bucket: '2min' },
  '24h': { from: () => Date.now() - 24 * 60 * 60_000, step: 600_000, bucket: '10min' },
};

export function AgentDetail() {
  const { id } = useParams<{ id: string }>();
  const [agent, setAgent] = useState<AgentSummary | null>(null);
  const [latest, setLatest] = useState<Awaited<ReturnType<typeof Agents.latest>> | null>(null);
  const [system, setSystem] = useState<SystemPoint[]>([]);
  const [disks, setDisks] = useState<DiskPoint[]>([]);
  const [networks, setNetworks] = useState<NetPoint[]>([]);
  const [processes, setProcesses] = useState<ProcPoint[]>([]);
  const [docker, setDocker] = useState<DockerPoint[]>([]);
  const [http, setHttp] = useState<HttpPoint[]>([]);
  const [probes, setProbes] = useState<HttpProbeConfig[]>([]);
  const [services, setServices] = useState<ServiceWatchConfig[]>([]);
  const [tab, setTab] = useState<'overview' | 'disk' | 'network' | 'procs' | 'docker' | 'http' | 'services'>('overview');
  const [range, setRange] = useState<Range>('1h');

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      Agents.get(id),
      Agents.latest(id),
      Agents.history<SystemPoint>(id, 'system'),
      Agents.history<DiskPoint>(id, 'disk'),
      Agents.history<NetPoint>(id, 'network'),
      Agents.history<ProcPoint>(id, 'process'),
      Agents.history<DockerPoint>(id, 'docker'),
      Agents.history<HttpPoint>(id, 'http'),
      Probes.list(id),
      ServicesApi.list(id),
    ])
      .then(([agentR, latestR, sysR, diskR, netR, procR, dockerR, httpR, probesR, servicesR]) => {
        setAgent(agentR.agent);
        setLatest(latestR);
        setSystem(sysR.points);
        setDisks(diskR.points);
        setNetworks(netR.points);
        setProcesses(procR.points);
        setDocker(dockerR.points);
        setHttp(httpR.points);
        setProbes(probesR.probes);
        setServices(servicesR.services);
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-refresh a cada 15s.
  useEffect(() => {
    if (!id) return;
    const interval = setInterval(async () => {
      try {
        const [latestR, sysR, httpR] = await Promise.all([
          Agents.latest(id),
          Agents.history<SystemPoint>(id, 'system', { from: RANGES[range].from(), to: Date.now() }),
          Agents.history<HttpPoint>(id, 'http', { from: RANGES[range].from(), to: Date.now() }),
        ]);
        setLatest(latestR);
        setSystem(sysR.points);
        setHttp(httpR.points);
      } catch { /* ignore */ }
    }, 15_000);
    return () => clearInterval(interval);
  }, [id, range]);

  if (loading) return <Spinner />;
  if (!agent) return <EmptyState title="Agente não encontrado" hint="Volte para a lista de agentes." />;

  const systemByTime = system;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">{agent.name}</h2>
          <div className="text-xs text-slate-400 font-mono flex gap-3 mt-1">
            <span>{agent.hostname ?? '—'}</span>
            <span>{agent.os ?? '—'}</span>
            <span>·</span>
            <span className={clsx(latest?.online ? 'text-ok' : 'text-danger')}>{latest?.online ? '🟢 Online' : '🔴 Offline'}</span>
            {agent.lastSeenAt && <span>· visto {fmtAgo(agent.lastSeenAt)}</span>}
          </div>
        </div>
        <div className="flex-1" />
        <RangePicker range={range} onChange={setRange} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="CPU" value={latest?.system ? `${latest.system.cpuPct.toFixed(0)}%` : '—'} tone={tone(latest?.system?.cpuPct ?? null)} sub={latest?.system ? `load ${latest.system.load1.toFixed(2)}` : 'Sem dados'} />
        <Stat label="Memória" value={latest?.system ? `${latest.system.memPct.toFixed(0)}%` : '—'} tone={tone(latest?.system?.memPct ?? null)} sub={latest?.system ? `${prettyBytes(latest.system.memUsed)} / ${prettyBytes(latest.system.memTotal)}` : '—'} />
        <Stat label="Disco mais cheio" value={latest?.disks?.length ? `${Math.max(...latest.disks.map((d) => d.pct)).toFixed(0)}%` : '—'} tone={tone(maxDiskPct(latest?.disks ?? []))} sub={latest?.disks?.length ? mountOfMax(latest.disks) : '—'} />
        <Stat label="Containers Docker" value={String(latest?.docker?.length ?? 0)} sub={latest?.docker?.length ? `${latest!.docker!.filter((c) => c.status === 'running').length} rodando` : 'Sem dados'} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {([
          ['overview', 'Geral'], ['disk', 'Discos'], ['network', 'Rede'],
          ['procs', 'Processos'], ['docker', 'Docker'], ['http', 'HTTP / SSL'], ['services', 'Serviços'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx('btn justify-center', tab === key && 'border-accent text-accent')}
          >{label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <Card title="Uso de CPU e Memória" sub="Janela recente">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={systemByTime}>
                <defs>
                  <linearGradient id="cpu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5eead4" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#5eead4" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="mem" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1f2a44" vertical={false} />
                <XAxis dataKey="ts" tickFormatter={fmtTick} stroke="#64748b" fontSize={11} />
                <YAxis domain={[0, 100]} stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtTick} />
                <Legend />
                <Area type="monotone" name="CPU %" dataKey="cpuPct" stroke="#5eead4" fill="url(#cpu)" strokeWidth={2} />
                <Area type="monotone" name="Memória %" dataKey="memPct" stroke="#3b82f6" fill="url(#mem)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Load Average (1 min)">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={systemByTime}>
                <CartesianGrid stroke="#1f2a44" vertical={false} />
                <XAxis dataKey="ts" tickFormatter={fmtTick} stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtTick} />
                <Line type="monotone" name="load1" dataKey="load1" stroke="#eab308" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {tab === 'disk' && (
        <Card title="Uso de disco por ponto de montagem">
          {disks.length === 0 ? <EmptyState title="Sem dados de disco ainda" /> : (
            <div className="space-y-4">
              {Object.entries(groupBy(disks, (d) => d.mount)).map(([mount, points]) => {
                const last = points[points.length - 1] ?? null;
                return (
                  <div key={mount}>
                    <div className="flex items-baseline justify-between text-sm mb-1">
                      <span className="font-mono text-slate-300">{mount}</span>
                      {last && (
                        <span className="text-xs text-slate-400">
                          {prettyBytes(last.used)} / {prettyBytes(last.total)} · {last.pct.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <ProgressBar pct={(last?.pct ?? 0)} tone={tone(last?.pct ?? null)} />
                    <div className="h-12 mt-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={points}>
                          <Line type="monotone" dataKey="pct" stroke="#5eead4" dot={false} strokeWidth={1.5} />
                          <YAxis hide domain={[0, 100]} />
                          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtTick} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === 'network' && (
        <Card title="Tráfego de rede por interface">
          {networks.length === 0 ? <EmptyState title="Sem dados de rede ainda" /> : (
            <div className="grid gap-4">
              {Object.entries(groupBy(networks, (n) => n.iface)).map(([iface, points]) => (
                <div key={iface}>
                  <div className="text-sm font-mono text-slate-300 mb-1">{iface}</div>
                  <ResponsiveContainer width="100%" height={120}>
                    <AreaChart data={points.map((p, i, arr) => ({
                      ...p,
                      rxLabel: prettyBytes(p.rxSec) + '/s',
                      txLabel: prettyBytes(p.txSec) + '/s',
                      first: i === 0 ? 0 : arr[i - 1].rxSec,
                      firstTx: i === 0 ? 0 : arr[i - 1].txSec,
                      dx: Math.max(0, p.rxSec - (i === 0 ? 0 : arr[i - 1].rxSec)),
                      dtx: Math.max(0, p.txSec - (i === 0 ? 0 : arr[i - 1].txSec)),
                    }))}>
                      <defs>
                        <linearGradient id={`rx-${iface}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22c55e" stopOpacity={0.6}/>
                          <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05}/>
                        </linearGradient>
                        <linearGradient id={`tx-${iface}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ef4444" stopOpacity={0.6}/>
                          <stop offset="100%" stopColor="#ef4444" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#1f2a44" vertical={false} />
                      <XAxis dataKey="ts" tickFormatter={fmtTick} stroke="#64748b" fontSize={11}/>
                      <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v: number) => prettyBytes(v)} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtTick} formatter={(v: unknown) => prettyBytes(Number(v)) + '/s' as unknown as string} />
                      <Area type="monotone" name="RX" dataKey="rxSec" stroke="#22c55e" fill={`url(#rx-${iface})`} strokeWidth={2} />
                      <Area type="monotone" name="TX" dataKey="txSec" stroke="#ef4444" fill={`url(#tx-${iface})`} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'procs' && (
        <Card title="Top processos (última coleta)">
          {!latest?.topProcs?.length ? <EmptyState title="Sem dados de processos ainda" /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>PID</th><th>Nome</th><th>CPU</th><th>Memória</th><th>Comando</th></tr></thead>
                <tbody>
                  {latest.topProcs.map((p) => (
                    <tr key={p.pid}>
                      <td className="font-mono text-xs">{p.pid}</td>
                      <td className="font-medium text-white">{p.name}</td>
                      <td className="w-40"><div className="flex items-center gap-2"><ProgressBar pct={p.cpuPct} tone={tone(p.cpuPct)} /><span className="text-xs font-mono w-12 text-right">{p.cpuPct.toFixed(0)}%</span></div></td>
                      <td className="w-40"><div className="flex items-center gap-2"><ProgressBar pct={p.memPct} tone={tone(p.memPct)} /><span className="text-xs font-mono w-12 text-right">{p.memPct.toFixed(0)}%</span></div></td>
                      <td className="font-mono text-xs text-slate-500 max-w-[28rem] truncate">{p.cmd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'docker' && (
        <Card title="Containers Docker">
          {!latest?.docker?.length ? <EmptyState title="Nenhum container detectado" hint="O agente coleta via Docker socket; garanta permissões." /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>Container</th><th>Imagem</th><th>Status</th><th>CPU</th><th>Memória</th></tr></thead>
                <tbody>
                  {latest.docker.map((c) => (
                    <tr key={c.containerId}>
                      <td>
                        <div className="font-medium text-white">{c.name}</div>
                        <div className="font-mono text-[10px] text-slate-500">{c.containerId.slice(0, 12)}</div>
                      </td>
                      <td className="font-mono text-xs text-slate-400 max-w-xs truncate">{c.image ?? '—'}</td>
                      <td>{statusPill(c.status)}</td>
                      <td className="w-40"><div className="flex items-center gap-2"><ProgressBar pct={c.cpuPct} tone={tone(c.cpuPct)} /><span className="text-xs font-mono w-12 text-right">{c.cpuPct.toFixed(0)}%</span></div></td>
                      <td>{prettyBytes(c.memUsed)} / {prettyBytes(c.memLimit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'http' && (
        <div className="space-y-4">
          <Card title="Probes HTTP ativas" sub="Status e SSL">
            {probes.length === 0 ? <EmptyState title="Nenhum probe configurado" hint="Adicione em 'Sites' para começar a monitorar suas URLs." /> : (
              <div className="overflow-x-auto -mx-5">
                <table className="table">
                  <thead><tr><th>Rótulo</th><th>URL</th><th>Intervalo</th><th>Último</th><th>SSL</th></tr></thead>
                  <tbody>
                    {probes.map((p) => {
                      const last = http.filter((h) => h.probeId === p.id).slice(-1)[0];
                      return (
                        <tr key={p.id}>
                          <td className="font-medium text-white">{p.label}</td>
                          <td className="font-mono text-xs text-slate-400">{p.url}</td>
                          <td className="text-slate-400 text-xs">{p.intervalSec}s</td>
                          <td>
                            {last ? (
                              last.ok
                                ? <Pill tone="ok">{last.statusCode} · {last.latencyMs?.toFixed(0)}ms</Pill>
                                : <Pill tone="error">{last.statusCode ?? 'fail'} · {last.error ?? 'fora'}</Pill>
                            ) : <Pill tone="muted">aguardando</Pill>}
                          </td>
                          <td>{last?.sslDaysLeft != null ? <Pill tone={last.sslDaysLeft < 14 ? 'warn' : 'ok'}>{last.sslDaysLeft}d</Pill> : <Pill tone="muted">—</Pill>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Card title="Latência HTTP (5 últimas coletas por probe)" sub="Tempo total da requisição">
            {http.length === 0 ? <EmptyState title="Sem medições ainda" /> : (
              <div className="space-y-3">
                {probes.map((p) => {
                  const points = http.filter((h) => h.probeId === p.id).slice(-30).map((h) => ({
                    ts: h.ts, latencyMs: h.latencyMs ?? 0,
                  }));
                  if (points.length === 0) return null;
                  return (
                    <div key={p.id}>
                      <div className="text-sm font-medium text-white mb-1">{p.label} <span className="font-mono text-xs text-slate-500">{p.url}</span></div>
                      <ResponsiveContainer width="100%" height={120}>
                        <LineChart data={points}>
                          <CartesianGrid stroke="#1f2a44" vertical={false} />
                          <XAxis dataKey="ts" tickFormatter={fmtTick} stroke="#64748b" fontSize={11} />
                          <YAxis stroke="#64748b" fontSize={11} />
                          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtTick} />
                          <Line type="monotone" name="latência (ms)" dataKey="latencyMs" stroke="#5eead4" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'services' && (
        <Card title="Serviços systemd monitorados">
          {services.length === 0 ? <EmptyState title="Nenhum serviço configurado" hint="Adicione em 'Sites' (na seção de serviços)." /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>Nome</th><th>Estado atual</th></tr></thead>
                <tbody>
                  {services.map((sw) => {
                    const live = latest?.services?.find((s) => s.name === sw.name);
                    return (
                      <tr key={sw.id}>
                        <td className="font-mono text-white">{sw.name}</td>
                        <td>
                          {live
                            ? <Pill tone={live.active ? 'ok' : 'error'}>{live.status}</Pill>
                            : <Pill tone="muted">aguardando</Pill>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Card({ title, sub, children, right }: { title: string; sub?: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <section className="card">
      <div className="card-header">
        <div>
          <h3 className="font-semibold">{title}</h3>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
        {right}
      </div>
      <div className="card-body">{children}</div>
    </section>
  );
}

function RangePicker({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  return (
    <div className="flex items-center gap-1 bg-bg-subtle border border-border rounded-lg p-1">
      {(['15m', '1h', '6h', '24h'] as Range[]).map((r) => (
        <button key={r} onClick={() => onChange(r)} className={clsx('px-3 py-1.5 text-xs rounded-md transition', range === r ? 'bg-accent/15 text-accent' : 'text-slate-400 hover:text-white')}>{r}</button>
      ))}
    </div>
  );
}

function tone(pct: number | null): 'ok' | 'warn' | 'error' {
  if (pct == null) return 'ok';
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warn';
  return 'ok';
}
function statusPill(status: string) {
  if (status === 'running') return <Pill tone="ok">{status}</Pill>;
  if (status === 'exited' || status === 'dead') return <Pill tone="error">{status}</Pill>;
  if (status === 'paused' || status === 'restarting') return <Pill tone="warn">{status}</Pill>;
  return <Pill tone="muted">{status}</Pill>;
}
function maxDiskPct(disks: Array<{ pct: number }>): number | null {
  if (!disks.length) return null;
  return Math.max(...disks.map((d) => d.pct));
}
function mountOfMax(disks: Array<{ pct: number; mount: string }>): string {
  if (!disks.length) return '—';
  return disks.reduce((a, b) => (a.pct >= b.pct ? a : b)).mount;
}
function fmtTick(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
function fmtAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'agora';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m atrás`;
  return `${Math.floor(diff / 3_600_000)}h atrás`;
}
function prettyBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 1)} ${units[i]}`;
}
function groupBy<T, K extends string | number>(arr: T[], key: (t: T) => K): Record<K, T[]> {
  const acc = {} as Record<K, T[]>;
  for (const item of arr) {
    const k = key(item);
    (acc[k] ||= []).push(item);
  }
  return acc;
}
const tooltipStyle = { background: '#111a2e', border: '1px solid #1f2a44', borderRadius: '8px', fontSize: '12px' } as const;

// suprimir warning recharts defaultProps em React 18 (já não há mas evita alerta futuro).
const _ = useMemo;
void _;
